import { isEmpty } from 'lodash';

import { createUrl, restGet, restPatch, restPost } from '~/src/dash/api/rest';

export interface ISmartFloorConfig {
  Active: boolean;
  ControlTrafficPercentage: number;
  CreatedAt: string;
  DeletedAt: string | null;
  HeaderbidderID: number;
  ID: number;
  IsABTestEnabled: boolean;
  IsMaxBidFloorPercentage: boolean;
  IsMinBidFloorPercentage: boolean;
  IsTestIncrementHighPercentage: boolean;
  IsTestIncrementLowPercentage: boolean;
  MaxBidFloor: number;
  MinBidFloor: number;
  SmartFloors: ISmartFloorPayloadUI[];
  TestIncrementHigh: number;
  TestIncrementLow: number;
  ThompsonSamplingEnabled: boolean;
  UpdatedAt: string | null;
}

export interface ISmartFloorConfigUI extends ISmartFloorConfig {
  shouldAllowSortingTable?: boolean;
}

export interface ISmartFloorPayload {
  Active: boolean;
  AssociatedChannelGroupIDs: Number[];
  BaseBidFloor: number;
  EstimatedRevenueUplift: number;
  EstimatedRevenueUplift12HourTrend?: number | null;
  HeaderbidderID: number;
  ID: number;
  IsTargetedThroughChannelAssociation: boolean;
  LastRestartTime: string | null;
  MaxBidFloor: number;
  MinBidFloor: number;
  OptimizedBidFloor: number;
  OptimizedFloor12HourTrend?: number | null;
  RestartVersion: number;
  SiteID: number;
  Status: string;
  TestIncrementHigh: number;
  TestIncrementLow: number;
  TestPeriod: number;
  ThompsonSamplingEnabled?: boolean;
}

export interface ISmartFloorPayloadUI extends ISmartFloorPayload {
  // For the ListView
  ChannelName?: string;
  ChannelGroupNames?: string[];
}

enum SmartFloorStatus {
  Unknown = 0,
  Pending = 1,
  Running = 2,
}

const SmartFloorStatusTextMap: Record<SmartFloorStatus, string> = {
  [SmartFloorStatus.Unknown]: 'Unknown',
  [SmartFloorStatus.Pending]: 'Pending',
  [SmartFloorStatus.Running]: 'Running',
};

export type smartFloorConfigCols =
  | 'BaseBidFloor'
  | 'IsABTestEnabled'
  | 'IsMaxBidFloorPercentage'
  | 'IsMinBidFloorPercentage'
  | 'isSiteIDSelected'
  | 'IsTestIncrementHighPercentage'
  | 'IsTestIncrementLowPercentage'
  | 'MaxBidFloor'
  | 'MinBidFloor'
  | 'SmartFloors'
  | 'TestIncrementHigh'
  | 'TestIncrementLow'
  | 'ThompsonSamplingEnabled';

export const smartFloorRecalcKeys = new Set([
  'SmartFloors',
  'BaseBidFloor',
  'IsTestIncrementHighPercentage',
  'IsTestIncrementLowPercentage',
  'TestIncrementHigh',
  'TestIncrementLow',
  'IsMaxBidFloorPercentage',
  'IsMinBidFloorPercentage',
  'MaxBidFloor',
  'MinBidFloor',
]);

export type SmartFloorsAPIKey =
  | 'Active'
  | 'AssociatedChannelGroupIDs'
  | 'BaseBidFloor'
  | 'CreatedAt'
  | 'DeletedAt'
  | 'EstimatedRevenueUplift'
  | 'EstimatedRevenueUplift12HourTrend' // TODO need these values from the cronjob
  | 'HeaderbidderID'
  | 'ID'
  | 'IsTargetedThroughChannelAssociation'
  | 'isSiteIDSelected'
  | 'LastRestartTime'
  | 'MaxBidFloor'
  | 'MinBidFloor'
  | 'OptimizedBidFloor'
  | 'OptimizedFloor12HourTrend'
  | 'RestartVersion'
  | 'SiteID'
  | 'Status'
  | 'TestIncrementHigh'
  | 'TestIncrementLow'
  | 'TestPeriod'
  | 'UpdatedAt';

/** genNewSmartFloorConfig
 * innitialize an empty config when smart floor is turned on for the first time
 */
export function genNewSmartFloorConfig(headerbidderID: number): ISmartFloorConfigUI {
  const smartFloorConfig: ISmartFloorConfigUI = {
    Active: true,
    ControlTrafficPercentage: 0,
    CreatedAt: '',
    DeletedAt: null,
    HeaderbidderID: headerbidderID,
    ID: 0,
    IsABTestEnabled: false,
    IsMaxBidFloorPercentage: false,
    IsMinBidFloorPercentage: false,
    IsTestIncrementHighPercentage: false,
    IsTestIncrementLowPercentage: false,
    MaxBidFloor: 0,
    MinBidFloor: 0,
    SmartFloors: [] as ISmartFloorPayloadUI[],
    TestIncrementHigh: 0,
    TestIncrementLow: 0,
    ThompsonSamplingEnabled: false,
    UpdatedAt: null,
  };
  return smartFloorConfig;
}

export function convertNumToFixedDecimalNum(num: number, fractionDigits: number): number {
  return Number(num.toFixed(fractionDigits));
}

interface ISmartFloorParams {
  MaxBidFloor: number;
  MinBidFloor: number;
  TestIncrementHigh: number;
  TestIncrementLow: number;
}

export function computeSmartFloorParamsFromConfig(
  smartFloorConfig: ISmartFloorConfigUI,
  baseFloor: number,
): ISmartFloorParams {
  const paramsFromConfig = {} as ISmartFloorParams;

  paramsFromConfig.TestIncrementHigh = convertNumToFixedDecimalNum(
    smartFloorConfig.TestIncrementHigh,
    2,
  );
  paramsFromConfig.TestIncrementLow = convertNumToFixedDecimalNum(
    smartFloorConfig.TestIncrementLow,
    2,
  );

  if (
    smartFloorConfig.IsTestIncrementHighPercentage ||
    smartFloorConfig.IsTestIncrementLowPercentage
  ) {
    paramsFromConfig.TestIncrementHigh = convertNumToFixedDecimalNum(
      (smartFloorConfig.TestIncrementHigh * baseFloor) / 100,
      2,
    );
    paramsFromConfig.TestIncrementLow = convertNumToFixedDecimalNum(
      (smartFloorConfig.TestIncrementLow * baseFloor) / 100,
      2,
    );
  }

  paramsFromConfig.MaxBidFloor = convertNumToFixedDecimalNum(smartFloorConfig.MaxBidFloor, 2);
  paramsFromConfig.MinBidFloor = convertNumToFixedDecimalNum(smartFloorConfig.MinBidFloor, 2);

  if (smartFloorConfig.IsMaxBidFloorPercentage || smartFloorConfig.IsMinBidFloorPercentage) {
    paramsFromConfig.MaxBidFloor = convertNumToFixedDecimalNum(
      baseFloor + (smartFloorConfig.MaxBidFloor * baseFloor) / 100,
      2,
    );
    paramsFromConfig.MinBidFloor = convertNumToFixedDecimalNum(
      baseFloor - (smartFloorConfig.MinBidFloor * baseFloor) / 100,
      2,
    );
  }

  return paramsFromConfig;
}

export function genNewSmartFloors(
  siteID: number,
  headerbidderID: number,
  baseFloor: number,
  smartFloorConfig: ISmartFloorConfigUI,
  channelGroupIDs: number[],
  isFromChannelSelect: boolean,
): ISmartFloorPayloadUI {
  const smartFloorParams = computeSmartFloorParamsFromConfig(smartFloorConfig, baseFloor);

  return {
    Active: true,
    AssociatedChannelGroupIDs: channelGroupIDs,
    BaseBidFloor: convertNumToFixedDecimalNum(baseFloor, 2),
    EstimatedRevenueUplift: 0,
    EstimatedRevenueUplift12HourTrend: 0,
    HeaderbidderID: headerbidderID,
    ID: 0,
    IsTargetedThroughChannelAssociation: isFromChannelSelect,
    LastRestartTime: null,
    MaxBidFloor: convertNumToFixedDecimalNum(smartFloorParams.MaxBidFloor, 2),
    MinBidFloor: convertNumToFixedDecimalNum(smartFloorParams.MinBidFloor, 2),
    OptimizedBidFloor: convertNumToFixedDecimalNum(baseFloor, 2),
    OptimizedFloor12HourTrend: 0,
    RestartVersion: 0,
    SiteID: siteID,
    Status: SmartFloorStatusTextMap[SmartFloorStatus.Pending],
    TestIncrementHigh: convertNumToFixedDecimalNum(smartFloorParams.TestIncrementHigh, 2),
    TestIncrementLow: convertNumToFixedDecimalNum(smartFloorParams.TestIncrementLow, 2),
    TestPeriod: 0,
    ThompsonSamplingEnabled: smartFloorConfig.ThompsonSamplingEnabled,
  } as ISmartFloorPayloadUI;
}

export function updateSmartFloorParamsFromConfig(
  smartFloorConfig: ISmartFloorConfigUI,
  baseFloor: number,
  smartFloor: ISmartFloorPayloadUI,
  selectedSiteIDToSelectedChannelGroupIDs: { [key: number]: Set<number> },
  selectedChannelIDs: Set<number>,
  shouldUpdateSmartFloorParams: boolean,
): ISmartFloorPayloadUI {
  smartFloor.AssociatedChannelGroupIDs = !isEmpty(
    selectedSiteIDToSelectedChannelGroupIDs[smartFloor.SiteID],
  )
    ? Array.from(selectedSiteIDToSelectedChannelGroupIDs[smartFloor.SiteID])
    : [];
  smartFloor.IsTargetedThroughChannelAssociation = selectedChannelIDs.has(smartFloor.SiteID);
  smartFloor.OptimizedBidFloor = baseFloor;
  smartFloor.BaseBidFloor = baseFloor;
  if (shouldUpdateSmartFloorParams) {
    const params = computeSmartFloorParamsFromConfig(smartFloorConfig, baseFloor);
    smartFloor.MaxBidFloor = params.MaxBidFloor;
    smartFloor.MinBidFloor = params.MinBidFloor;
    smartFloor.TestIncrementHigh = params.TestIncrementHigh;
    smartFloor.TestIncrementLow = params.TestIncrementLow;
  }

  return smartFloor;
}

/** getSmartFloorConfigs
 * retrieves smart floor configs for a specific publisher and bidder
 *
 * @param publisherID - The publisher identifier
 * @param bidderID - The bidder identifier
 * @param preloadSmartFloors - Optional flag to preload associated smart floors data
 * @returns Promise<ISmartFloorConfig[] | null | undefined> - Array of smart floor configurations
 *
 * HTTP Method: GET
 * URL Format: /smart_floor_configs?publisher_id={publisherID}&bidder_id={bidderID}&preload_smart_floors={preloadSmartFloors}
 */
export async function getSmartFloorConfigs(
  publisherID: string,
  bidderID: string,
  preloadSmartFloors: boolean = false,
): Promise<ISmartFloorConfig[] | null | undefined> {
  const reqUrl = `${createUrl(
    '/smart_floor_configs',
    publisherID,
  )}&bidder_id=${bidderID}&preload_smart_floors=${preloadSmartFloors}`;

  try {
    const res = await restGet<ISmartFloorConfig[]>(reqUrl);
    return res.parsedBody;
  } catch (e) {
    console.error(e);
    throw e;
  }
}

/** createSmartFloorConfig
 * creates a new smart floor config for a specific publisher and bidder
 */
export async function createSmartFloorConfig(
  publisherID: string,
  bidderID: string,
  smartFloorConfig: ISmartFloorConfig,
): Promise<ISmartFloorConfig | null | undefined> {
  const reqUrl = `${createUrl('/smart_floor_configs', publisherID)}&bidder_id=${bidderID}`;

  try {
    const res = await restPost<ISmartFloorConfig>(reqUrl, smartFloorConfig);
    if (isEmpty(res) || isEmpty(res.parsedBody)) {
      throw new Error('empty smart floors response');
    }
    return res.parsedBody;
  } catch (e) {
    console.error(e);
    return null;
  }
}

/** updateSmartFloorConfig
 * updates an existing smart floor config for a specific publisher and bidder
 */
export async function updateSmartFloorConfig(
  publisherID: string,
  bidderID: string,
  smartFloorConfig: ISmartFloorConfig,
): Promise<ISmartFloorConfig | null | undefined> {
  const reqUrl = `${createUrl(
    `/smart_floor_configs/${smartFloorConfig.ID}`,
    publisherID,
  )}&bidder_id=${bidderID}`;

  try {
    const res = await restPatch<ISmartFloorConfig>(reqUrl, smartFloorConfig);
    if (isEmpty(res) || isEmpty(res.parsedBody)) {
      throw new Error('empty smart floors response');
    }
    return res.parsedBody;
  } catch (e) {
    console.error(e);
    return null;
  }
}
